import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import config from '../../config';
import axios from "axios";

const CategoryUpdate = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [activeLanguage, setActiveLanguage] = useState('en');

  // English (default) form data
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Spanish translation form data
  const [spanishData, setSpanishData] = useState({
    name: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const response = await axios.get(`${config.BASE_URL}/api/admin/categories/admin/${id}`, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.data) {
          const cat = response.data.data;
          setFormData({
            name: cat.name || '',
            description: cat.description || ''
          });
          setSpanishData({
            name: cat.spanishData?.name || '',
            description: cat.spanishData?.description || ''
          });
        }
      } catch (err) {
        console.error('Error fetching category:', err);

        if (err.response?.status === 404) {
          setErrors({ submit: 'Category not found' });
        } else if (err.response?.status === 401) {
          setErrors({ submit: 'Authentication failed. Please login again.' });
          setTimeout(() => {
            navigate('/admin/login');
          }, 2000);
        } else {
          setErrors({ submit: 'Failed to fetch category details' });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [id, navigate]);

  const handleInputChange = (field, value) => {
   
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSpanishInputChange = (field, value) => {
    setSpanishData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        spanishData: JSON.stringify({
          name: spanishData.name?.trim(),
          description: spanishData.description?.trim()
        })
      };

      const response = await axios.put(`${config.BASE_URL}/api/admin/categories/${id}`, payload, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json"
        }
      });


      if (response.status === 200) {
        setSuccessMessage('Category updated successfully!');
        setTimeout(() => {
          navigate("/admin/category");
        }, 2000);
      }
    } catch (err) {
      console.error('Error updating category:', err);

      let errorMessage = "Failed to update category";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 400) {
        errorMessage = "Invalid category data provided";
      } else if (err.response?.status === 404) {
        errorMessage = "Category not found";
      } else if (err.response?.status === 409) {
        errorMessage = "Category name already exists";
      } else if (err.response?.status === 401) {
        errorMessage = "Authentication failed. Please login again.";
        setTimeout(() => {
          navigate('/admin/login');
        }, 2000);
      }

      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackButton = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="container">
          <div className="main-section">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="text-white">Loading category...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="container">
        <h1 className="dash_title">Dashboard</h1>
        <div className="main-section">
          <div className="back_btn_wrap">
            <button className="back-btn" onClick={handleBackButton}>Go Back</button>
          </div>
          <div className="main-inner add_project_main">
            {/* Language Tabs */}
            <div className="language-tabs" style={{ marginBottom: '20px' }}>
              <button 
                type="button" 
                className={`tab-btn ${activeLanguage === 'en' ? 'active' : ''}`}
                onClick={() => setActiveLanguage('en')}
                style={{
                  padding: '10px 20px',
                  marginRight: '10px',
                  backgroundColor: activeLanguage === 'en' ? '#007bff' : '#f8f9fa',
                  color: activeLanguage === 'en' ? 'white' : 'black',
                  border: '1px solid #ddd',
                  cursor: 'pointer'
                }}
              >
                English
              </button>
              <button 
                type="button" 
                className={`tab-btn ${activeLanguage === 'es' ? 'active' : ''}`}
                onClick={() => setActiveLanguage('es')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: activeLanguage === 'es' ? '#007bff' : '#f8f9fa',
                  color: activeLanguage === 'es' ? 'white' : 'black',
                  border: '1px solid #ddd',
                  cursor: 'pointer'
                }}
              >
                Spanish
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="language-content">
                {successMessage && (
                  <p className="success-message">{successMessage}</p>
                )}

                {/* English Content */}
                {activeLanguage === 'en' && (
                  <div className="language-content">
                    <h3>English Content</h3>
                    <div className="add_field titleField">
                      <h2>Category Name (English) *</h2>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter category name"
                        required
                        disabled={isSubmitting}
                      />
                      {errors.name && (
                        <p className="error-text">{errors.name}</p>
                      )}
                    </div>
                    <div className="add_field textareaField">
                      <h2>Description (English)</h2>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows="6"
                        placeholder="Enter category description (optional)"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                )}

                {/* Spanish Content */}
                {activeLanguage === 'es' && (
                  <div className="language-content">
                    <h3>Spanish Content</h3>
                    <div className="add_field titleField">
                      <h2>Category Name (Spanish) </h2>
                      <input
                        type="text"
                        name="name"
                        value={spanishData.name}
                        onChange={(e) => handleSpanishInputChange('name', e.target.value)}
                        placeholder="Ingrese el nombre de la categoría"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="add_field textareaField">
                      <h2>Description (Spanish)</h2>
                      <textarea
                        name="description"
                        value={spanishData.description}
                        onChange={(e) => handleSpanishInputChange('description', e.target.value)}
                        rows="6"
                        placeholder="Ingrese la descripción de la categoría (opcional)"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="add_project">
                <button type="submit" disabled={isSubmitting}>
                  <Save className="w-5 h-5" />
                  {isSubmitting ? 'Updating Category...' : 'Update Category'}
                </button>
              </div>
            </form>

            {errors.submit && (
              <p className="error-message">{errors.submit}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryUpdate;